import React, { useEffect } from 'react'
import styled from 'styled-components'
import { tryExtractPixelProperty } from '~/utils/css'

interface FillSliderWrapperProps {
  height?: number
  width?: number
  borderRadius?: number | string
  backgroundColor?: string
  border?: string
  borderStyle?: string
  borderColor?: string
  margin?: string
  outline?: string
  outlineColor?: string
}

const FillSliderWrapper = styled.div<FillSliderWrapperProps>`
  display: flex;
  height: ${(props) => props.height ?? 24}px;
  width: ${(props) => props.width ?? 100}px;
  background-color: ${(props) =>
    props.backgroundColor ?? 'var(--chakra-colors-bg-panel)'};
  margin: ${(props) => tryExtractPixelProperty(props.margin)};
  border: ${(props) => props.border ?? ''};
  border-color: ${(props) => props.borderColor ?? ''};
  border-radius: ${(props) =>
    tryExtractPixelProperty(props.borderRadius) ?? '8px'};
  border-style: ${(props) => props.borderStyle ?? ''};
  outline: ${(props) => props.outline ?? ''};
  outline-color: ${(props) => props.outlineColor ?? ''};
  justify-content: left;
  align-items: center;
  position: relative;
`

export interface FillSliderProps extends FillSliderWrapperProps {
  fillPercentage: number
  fillColor: string
  fillBorderRadius?: number | string
  fillBorder?: string
  fillBorderColor?: string
  fillOutline?: string
  fillOutlineColor?: string
  animate?: boolean
  animationDelay?: number
  animationTime?: number
  children?: React.ReactNode
}

export interface FillSliderFillProps {
  height?: number
  width?: number
  borderRadius?: number | string
  border?: string
  borderColor?: string
  fillColor: string
  animate?: boolean
  animationDelay?: number
  animationTime?: number
  outline?: string
  outlineColor?: string
}

const FillSliderFill = styled.div<FillSliderFillProps>`
  height: ${(props) => props.height ?? 24}px;
  width: ${(props) => props.width ?? 100}%;
  border: ${(props) => props.border ?? ''};
  border-color: ${(props) => props.borderColor ?? ''};
  border-radius: ${(props) =>
    tryExtractPixelProperty(props.borderRadius) ?? '8px'};
  background-color: ${(props) => props.fillColor};
  outline: ${(props) => props.outline ?? ''};
  outline-color: ${(props) => props.outlineColor ?? ''};
  transition: ${(props) =>
    props.animate
      ? `width ${props.animationTime?.toString() ?? 1}s ease-in-out`
      : ''};
  transition-delay: ${(props) => props.animationDelay ?? 0.1}s;
  display: flex;
`

const FillSlider = ({
  fillPercentage,
  fillColor,
  fillBorderRadius,
  fillBorder,
  fillBorderColor,
  fillOutline,
  fillOutlineColor,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  outline,
  outlineColor,
  height,
  width,
  margin,
  animate,
  animationDelay,
  animationTime,
  children,
}: FillSliderProps) => {
  const defaultAnimatedFillWidth = 0.005
  const [fillWidth, setFillWidth] = React.useState(
    fillPercentage >= defaultAnimatedFillWidth
      ? defaultAnimatedFillWidth
      : fillPercentage,
  )
  useEffect(() => {
    if (animate) {
      setFillWidth(fillPercentage)
    }
  }, [])
  return (
    <FillSliderWrapper
      height={height}
      width={width}
      margin={margin}
      borderRadius={borderRadius}
      border={border}
      borderColor={borderColor}
      borderStyle={borderStyle}
      backgroundColor={backgroundColor}
      outline={outline}
      outlineColor={outlineColor}
    >
      {fillPercentage > Number.EPSILON && (
        <FillSliderFill
          fillColor={fillColor}
          height={height}
          borderRadius={fillBorderRadius ?? borderRadius}
          border={fillBorder ?? ''}
          borderColor={fillBorderColor}
          width={(animate ? fillWidth : fillPercentage) * 100}
          animate={animate}
          animationDelay={animationDelay}
          animationTime={animationTime}
          outline={fillOutline}
          outlineColor={fillOutlineColor}
        />
      )}
      {children ?? ''}
    </FillSliderWrapper>
  )
}

export default FillSlider
